import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  DocumentSettingsFormGroup,
  InformationBoxComponent,
  SystemTemplateSelectorsComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  TypedFormControl,
  NgDialogModule,
  ButtonWithMenuComponent,
} from '@principle-theorem/ng-shared';
import {
  IDocumentSettings,
  IResolvedTemplateWithName,
  ITemplateDefinitionWithName,
  FONT_SIZE_PRESETS,
  LINE_HEIGHT_PRESETS,
  MARGIN_PRESETS,
  PAPER_SIZE_PRESETS,
  ORIENTATION_PRESETS,
  PARAGRAPH_SPACING_PRESETS,
} from '@principle-theorem/principle-core/interfaces';
import { CURATED_FONTS } from '@principle-theorem/editor';
import { compact } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { MatMenuModule } from '@angular/material/menu';

export enum DocumentPdfDialogMode {
  Print = 'print',
  Pdf = 'pdf',
}

export enum DocumentPdfAction {
  Print = 'print',
  Download = 'download',
  Save = 'save',
}

export interface IDocumentPdfDialogData {
  mode: DocumentPdfDialogMode;
  templates: ITemplateDefinitionWithName[];
  documentName: string;
  documentSettings?: IDocumentSettings;
}

export interface IDocumentPdfDialogResult {
  action: DocumentPdfAction;
  templates: IResolvedTemplateWithName[];
  fileName?: string;
  documentSettings?: IDocumentSettings;
}

@Component({
  selector: 'pr-document-pdf-dialog',
  templateUrl: './document-pdf-dialog.component.html',
  styleUrls: ['./document-pdf-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    NgDialogModule,
    SystemTemplateSelectorsComponent,
    ButtonWithMenuComponent,
    MatMenuModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    InformationBoxComponent,
  ],
})
export class DocumentPdfDialogComponent {
  selectedTemplates$ = new BehaviorSubject<IResolvedTemplateWithName[]>([]);
  fileNameControl = new TypedFormControl<string>('', Validators.required);

  curatedFonts = CURATED_FONTS;
  fontSizePresets = FONT_SIZE_PRESETS;
  lineHeightPresets = LINE_HEIGHT_PRESETS;
  paragraphSpacingPresets = PARAGRAPH_SPACING_PRESETS;
  marginPresets = MARGIN_PRESETS;
  paperSizePresets = PAPER_SIZE_PRESETS;
  orientationPresets = ORIENTATION_PRESETS;

  documentSettingsForm = new DocumentSettingsFormGroup();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IDocumentPdfDialogData,
    private _dialogRef: MatDialogRef<
      DocumentPdfDialogComponent,
      IDocumentPdfDialogResult
    >
  ) {
    this.fileNameControl.setValue(data.documentName);
    this.documentSettingsForm.setFromSettings(data.documentSettings);
  }

  get isPdfMode(): boolean {
    return this.data.mode === DocumentPdfDialogMode.Pdf;
  }

  close(): void {
    this._dialogRef.close();
  }

  print(): void {
    this._closeWith(DocumentPdfAction.Print);
  }

  downloadPdf(): void {
    this._closeWith(DocumentPdfAction.Download);
  }

  saveToPatient(): void {
    this._closeWith(DocumentPdfAction.Save);
  }

  resetToDefaults(): void {
    this.documentSettingsForm.setFromSettings(this.data.documentSettings);
  }

  private _closeWith(action: DocumentPdfAction): void {
    this._dialogRef.close({
      action,
      templates: compact(this.selectedTemplates$.value),
      fileName: this.isPdfMode ? this.fileNameControl.value : undefined,
      documentSettings: this.documentSettingsForm.toDocumentSettings(),
    });
  }
}
